/**
 * SPIN TENET — Construction Geometry State Slice
 *
 * Owned by React 19.2 and Redux Toolkit.
 * Manages lightweight, authoritative parametric construction geometry state.
 *
 * ARCHITECTURAL INVARIANT:
 * Strictly contains parametric metadata (id, type, center, width, depth, rotationY, height).
 * Zero vertex buffers, zero large numerical arrays, zero GPU handles.
 * Fully serializable and decoupled from CFD computational mesh and solver domain.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  RectangleGeometry,
  BuildingMassGeometry,
  ConstructionGeometry
} from '@contracts/index';
import { isValidRectangleGeometry, isValidBuildingMassHeight, isValidCircleGeometry } from '@contracts/index';

export type ConstructionTool = 'navigate' | 'rectangle' | 'select';

export interface GeometryState {
  entities: Record<string, ConstructionGeometry>;
  ids: string[];
  selectedId: string | null;
  activeTool: ConstructionTool;
}

const initialState: GeometryState = {
  entities: {},
  ids: [],
  selectedId: null,
  activeTool: 'navigate'
};

export const geometrySlice = createSlice({
  name: 'geometry',
  initialState,
  reducers: {
    addRectangle: (state, action: PayloadAction<RectangleGeometry>) => {
      const rect = action.payload;
      state.entities[rect.id] = rect;
      if (!state.ids.includes(rect.id)) {
        state.ids.push(rect.id);
      }
      state.selectedId = rect.id;
    },
    addGeometry: (state, action: PayloadAction<ConstructionGeometry>) => {
      const geom = action.payload;
      state.entities[geom.id] = geom;
      if (!state.ids.includes(geom.id)) {
        state.ids.push(geom.id);
      }
      state.selectedId = geom.id;
    },
    extrudeGeometry: (
      state,
      action: PayloadAction<{ id: string; height: number }>
    ) => {
      const { id, height } = action.payload;
      const existing = state.entities[id];
      if (!existing || !isValidBuildingMassHeight(height)) return;

      if (existing.type === 'rectangle') {
        const mass: BuildingMassGeometry = {
          id: existing.id,
          type: 'building_mass',
          center: existing.center,
          width: existing.width,
          depth: existing.depth,
          rotationY: existing.rotationY,
          height
        };
        state.entities[id] = mass;
      } else if (existing.type === 'building_mass') {
        existing.height = height;
      } else {
        existing.height = height;
      }
    },
    flattenGeometry: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const existing = state.entities[id];
      if (!existing) return;
      if (existing.type === 'building_mass') {
        const rect: RectangleGeometry = {
          id: existing.id,
          type: 'rectangle',
          center: existing.center,
          width: existing.width,
          depth: existing.depth,
          rotationY: existing.rotationY
        };
        state.entities[id] = rect;
      } else {
        delete existing.height;
      }
    },
    updateCircleRadius: (
      state,
      action: PayloadAction<{ id: string; radius: number }>
    ) => {
      const { id, radius } = action.payload;
      const existing = state.entities[id];
      if (existing && existing.type === 'circle' && isValidCircleGeometry(radius)) {
        existing.radius = radius;
        existing.width = radius * 2;
        existing.depth = radius * 2;
      }
    },
    updateSplinePoints: (
      state,
      action: PayloadAction<{ id: string; points: import('@contracts/index').GroundPoint[] }>
    ) => {
      const { id, points } = action.payload;
      const existing = state.entities[id];
      if (existing && existing.type === 'spline' && points.length >= 2) {
        existing.points = points;
      }
    },
    extrudeRectangle: (
      state,
      action: PayloadAction<{ id: string; height: number }>
    ) => {
      const { id, height } = action.payload;
      const existing = state.entities[id];
      if (
        existing &&
        isValidBuildingMassHeight(height) &&
        'width' in existing &&
        existing.width !== undefined &&
        'depth' in existing &&
        existing.depth !== undefined
      ) {
        const mass: BuildingMassGeometry = {
          id: existing.id,
          type: 'building_mass',
          center: existing.center,
          width: existing.width,
          depth: existing.depth,
          rotationY: existing.rotationY,
          height
        };
        state.entities[id] = mass;
      }
    },
    updateBuildingHeight: (
      state,
      action: PayloadAction<{ id: string; height: number }>
    ) => {
      const { id, height } = action.payload;
      const existing = state.entities[id];
      if (existing && existing.type === 'building_mass' && isValidBuildingMassHeight(height)) {
        existing.height = height;
      }
    },
    updateBuildingWidth: (
      state,
      action: PayloadAction<{ id: string; width: number }>
    ) => {
      const { id, width } = action.payload;
      const existing = state.entities[id];
      if (
        existing &&
        'depth' in existing &&
        existing.depth !== undefined &&
        isValidRectangleGeometry(width, existing.depth)
      ) {
        existing.width = width;
      }
    },
    updateBuildingDepth: (
      state,
      action: PayloadAction<{ id: string; depth: number }>
    ) => {
      const { id, depth } = action.payload;
      const existing = state.entities[id];
      if (
        existing &&
        'width' in existing &&
        existing.width !== undefined &&
        isValidRectangleGeometry(existing.width, depth)
      ) {
        existing.depth = depth;
      }
    },
    updateBuildingDimensions: (
      state,
      action: PayloadAction<{
        id: string;
        width?: number;
        depth?: number;
        height?: number;
      }>
    ) => {
      const { id, width, depth, height } = action.payload;
      const existing = state.entities[id];
      if (!existing || existing.type !== 'building_mass') return;

      const targetWidth = width !== undefined ? width : existing.width;
      const targetDepth = depth !== undefined ? depth : existing.depth;
      const targetHeight = height !== undefined ? height : existing.height;

      // Dimension validation: width >= 3.048, depth >= 3.048, height > 0
      if (
        isValidRectangleGeometry(targetWidth, targetDepth) &&
        isValidBuildingMassHeight(targetHeight)
      ) {
        // Enforce invariants: center.x, center.y (strictly 0), center.z, and rotationY remain strictly invariant
        existing.width = targetWidth;
        existing.depth = targetDepth;
        existing.height = targetHeight;
      }
    },
    updateDimensions: (
      state,
      action: PayloadAction<{
        id: string;
        width?: number;
        depth?: number;
        height?: number;
      }>
    ) => {
      const { id, width, depth, height } = action.payload;
      const existing = state.entities[id];
      if (
        !existing ||
        !('width' in existing) ||
        existing.width === undefined ||
        !('depth' in existing) ||
        existing.depth === undefined
      )
        return;

      const targetWidth = width !== undefined ? width : existing.width;
      const targetDepth = depth !== undefined ? depth : existing.depth;

      if (!isValidRectangleGeometry(targetWidth, targetDepth)) return;

      if (existing.type === 'building_mass') {
        const targetHeight = height !== undefined ? height : existing.height;
        if (!isValidBuildingMassHeight(targetHeight)) return;
        existing.width = targetWidth;
        existing.depth = targetDepth;
        existing.height = targetHeight;
      } else {
        existing.width = targetWidth;
        existing.depth = targetDepth;
      }
    },
    moveGeometry: (
      state,
      action: PayloadAction<{
        id: string;
        center: { x: number; y: 0; z: number };
      }>
    ) => {
      const { id, center } = action.payload;
      const existing = state.entities[id];
      if (existing && Number.isFinite(center.x) && Number.isFinite(center.z)) {
        // Enforce state invariants:
        // ONLY center.x and center.z change.
        // center.y strictly 0.
        // width, depth, height, and rotationY remain strictly invariant.
        existing.center = {
          x: center.x,
          y: 0,
          z: center.z
        };
      }
    },
    deleteGeometry: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.entities[id];
      state.ids = state.ids.filter((i) => i !== id);
      if (state.selectedId === id) {
        state.selectedId = null;
      }
    },
    removeGeometry: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.entities[id];
      state.ids = state.ids.filter((i) => i !== id);
      if (state.selectedId === id) {
        state.selectedId = null;
      }
    },
    // Backward-compatible alias
    removeRectangle: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      delete state.entities[id];
      state.ids = state.ids.filter((i) => i !== id);
      if (state.selectedId === id) {
        state.selectedId = null;
      }
    },
    selectGeometry: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setActiveTool: (state, action: PayloadAction<ConstructionTool>) => {
      state.activeTool = action.payload;
    },
    clearGeometry: (state) => {
      state.entities = {};
      state.ids = [];
      state.selectedId = null;
    }
  }
});

export const {
  addRectangle,
  addGeometry,
  extrudeGeometry,
  flattenGeometry,
  updateCircleRadius,
  updateSplinePoints,
  extrudeRectangle,
  updateBuildingHeight,
  updateBuildingWidth,
  updateBuildingDepth,
  updateBuildingDimensions,
  updateDimensions,
  moveGeometry,
  deleteGeometry,
  removeGeometry,
  removeRectangle,
  selectGeometry,
  setActiveTool,
  clearGeometry
} = geometrySlice.actions;

export default geometrySlice.reducer;
